import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Form, Input, Button } from "antd";
import axios from "axios";
import { toast } from "react-toastify";
import { apiUrl } from "../../Constant";
import {
  setIsLoginLoading,
  setToken,
  setUserID,
  setUserName,
} from "../../Features/User/User";
import styles from "./AuthGate.module.scss";

function AuthGate({ children }) {
  const dispatch = useDispatch();
  const reduxToken = useSelector((state) => state.user?.token);
  const token = reduxToken || localStorage.getItem("token");

  // "login" | "signup" | "signupComplete" | "forgot" | "forgotSent" | "reset" | "resetSuccess"
  const [view, setView] = useState("login");
  const [loginForm] = Form.useForm();
  const [signupForm] = Form.useForm();
  const [forgotForm] = Form.useForm();
  const [resetForm] = Form.useForm();
  const loginLoading = useSelector((state) => state.user?.isLoginLoading);
  const [signupLoading, setSignupLoading] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [loginErrorType, setLoginErrorType] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [resetEmail, setResetEmail] = useState("");

  // Reset loading state on mount — redux-persist can restore isLoginLoading:true if user
  // refreshed mid-request, leaving the button permanently disabled
  useEffect(() => {
    dispatch(setIsLoginLoading(false));
  }, []); // eslint-disable-line

  // Check if arriving from a password reset link
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get("token");
    const e = params.get("email");
    if (window.location.pathname === "/reset-password" && t && e) {
      setResetToken(t);
      setResetEmail(e);
      setView("reset");
    }
  }, []);

  // Admin, factory, and Tennis Necklace concept studio routes bypass the auth gate
  if (
    window.location.pathname.startsWith("/admin") ||
    window.location.pathname.startsWith("/factory") ||
    window.location.pathname.startsWith("/tennis-necklaces")
  ) {
    return children;
  }

  if (token) {
    return children;
  }

  /* ─── Handlers ─── */

  const handleLogin = async (values) => {
    const payload = {
      email: values.emailOrPhone.includes("@") ? values.emailOrPhone : undefined,
      phoneNumber: !values.emailOrPhone.includes("@") ? values.emailOrPhone : undefined,
      password: values.password,
    };

    try {
      dispatch(setIsLoginLoading(true));
      setLoginError("");
      setLoginErrorType("");
      // withCredentials: true so the tdc_device cookie travels with the
      // request — login uses it to enforce the per-user device lock.
      const res = await axios.post(apiUrl.LOGIN, payload, { withCredentials: true });

      if (res.data.type === "success") {
        const { token: authToken, user } = res.data;
        localStorage.setItem("token", authToken);
        localStorage.setItem("userId", user.id);
        dispatch(setToken(authToken));
        dispatch(setUserID(user.id));
        dispatch(setUserName(user.name));
        setLoginError("");
        const firstName = user.name ? user.name.split(" ")[0] : "";
        toast.success(`Welcome back, ${firstName}!`);
      }
    } catch (error) {
      const status = error?.response?.status;
      const code = error?.response?.data?.code;
      const msg = error?.response?.data?.message || "Login failed";
      setLoginError(msg);

      if (status === 403 && (code === "device_locked" || code === "device_limit_reached")) {
        setLoginErrorType("device_locked");
      } else if (status === 403 && msg.includes("restricted")) {
        setLoginErrorType("blocked");
      } else if (status === 403) {
        setLoginErrorType("pending");
      } else {
        setLoginErrorType("");
      }
    } finally {
      dispatch(setIsLoginLoading(false));
    }
  };

  const handleSignup = async (values) => {
    try {
      setSignupLoading(true);
      const payload = {
        name: values.name,
        email: values.email,
        password: values.password,
        phoneNumber: values.phone,
        companyName: values.company,
      };

      const res = await axios.post(apiUrl.SIGNUP, payload);

      if (res.data.type === "success") {
        signupForm.resetFields();
        setView("signupComplete");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Signup failed");
    } finally {
      setSignupLoading(false);
    }
  };

  const handleForgot = async (values) => {
    try {
      setForgotLoading(true);
      await axios.post(apiUrl.FORGOT_PASSWORD, { email: values.email });
      setView("forgotSent");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Something went wrong");
    } finally {
      setForgotLoading(false);
    }
  };

  const handleReset = async (values) => {
    if (values.password !== values.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    try {
      setResetLoading(true);
      const res = await axios.post(apiUrl.RESET_PASSWORD, {
        email: resetEmail,
        token: resetToken,
        password: values.password,
      });
      if (res.data.type === "success") {
        setView("resetSuccess");
        // Clean URL
        window.history.replaceState({}, "", "/");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Reset failed. The link may have expired.");
    } finally {
      setResetLoading(false);
    }
  };

  const goToLogin = () => {
    setView("login");
    setLoginError("");
    setLoginErrorType("");
  };

  /* ─── Welcome text ─── */

  const getWelcomeText = () => {
    switch (view) {
      case "signup": return "Create your trade account";
      case "forgot": return "Reset your password";
      case "forgotSent": return "Check your email";
      case "reset": return "Set a new password";
      case "resetSuccess": return "Password updated";
      case "signupComplete": return "Application submitted";
      default: return "Sign in to access our trade catalogue";
    }
  };

  /* ─── Show tabs only for login/signup ─── */

  const showTabs = view === "login" || view === "signup";

  return (
    <div className={styles.gate}>
      <div className={styles.particles}>
        {[...Array(16)].map((_, i) => (
          <span key={i} className={styles.particle} />
        ))}
      </div>

      <div className={styles.card}>
      <div className={styles.logoWrap}>
        <img src="/logo.png" alt="The Diamond Counter" className={styles.logo} />
      </div>

      <div className={styles.divider} />

      <p className={styles.welcome}>{getWelcomeText()}</p>

        {/* Tabs — only on login/signup */}
        {showTabs && (
          <div className={styles.tabs}>
            <button
              className={`${styles.tab} ${view === "login" ? styles.tabActive : ""}`}
              onClick={goToLogin}
            >
              Sign In
            </button>
            <button
              className={`${styles.tab} ${view === "signup" ? styles.tabActive : ""}`}
              onClick={() => setView("signup")}
            >
              Create Account
            </button>
          </div>
        )}

        {/* ═══ LOGIN ═══ */}
        {view === "login" && (
          <Form form={loginForm} name="gate-login" onFinish={handleLogin} layout="vertical" className={styles.form}>
            <Form.Item name="emailOrPhone" rules={[{ required: true, message: "Please enter email or phone" }]}>
              <Input placeholder="Email or Phone Number" size="large" />
            </Form.Item>

            <Form.Item name="password" rules={[{ required: true, message: "Please enter password" }]}>
              <Input.Password placeholder="Password" size="large" />
            </Form.Item>

            {loginErrorType === "blocked" && (
              <div style={{ background: "#FFF3F3", border: "1px solid #e74c3c", padding: "14px 18px", marginBottom: 16, fontSize: 13, color: "#333", lineHeight: 1.6 }}>
                <strong style={{ color: "#e74c3c" }}>Account Restricted</strong>
                <p style={{ margin: "6px 0 0" }}>
                  Your account has been temporarily restricted. Please contact us at{" "}
                  <a href="mailto:sales@thediamondcounter.com" style={{ color: "#C5A028", fontWeight: 600 }}>sales@thediamondcounter.com</a>
                </p>
              </div>
            )}

            {loginErrorType === "pending" && (
              <div style={{ background: "#FFF9E6", border: "1px solid #C5A028", padding: "14px 18px", marginBottom: 16, fontSize: 13, color: "#333", lineHeight: 1.6 }}>
                <strong style={{ color: "#C5A028" }}>Account Under Review</strong>
                <p style={{ margin: "6px 0 0" }}>
                  Your account is being reviewed. We typically approve new accounts within <strong>24 hours</strong>. You'll receive an email once approved.
                </p>
              </div>
            )}

            {loginErrorType === "device_locked" && (
              <div style={{ background: "#FFF3F3", border: "1px solid #e74c3c", padding: "14px 18px", marginBottom: 16, fontSize: 13, color: "#333", lineHeight: 1.6 }}>
                <strong style={{ color: "#e74c3c" }}>Locked to another device</strong>
                <p style={{ margin: "6px 0 0" }}>
                  This account is signed in on a different device. Contact us at{" "}
                  <a href="mailto:sales@thediamondcounter.com" style={{ color: "#C5A028", fontWeight: 600 }}>sales@thediamondcounter.com</a>{" "}
                  to release the device lock if you're switching devices.
                </p>
              </div>
            )}

            {loginError && !loginErrorType && (
              <div style={{ padding: "14px 18px", background: "#FFF3F3", border: "1px solid #e74c3c", marginBottom: 16, fontSize: 13, color: "#c0392b", lineHeight: 1.5, fontWeight: 600 }}>
                {loginError}
              </div>
            )}

            <div style={{ textAlign: "right", marginBottom: 12 }}>
              <button
                type="button"
                onClick={() => setView("forgot")}
                style={{ background: "none", border: "none", color: "#C5A028", fontSize: 12, fontWeight: 600, cursor: "pointer", padding: 0 }}
              >
                Forgot Password?
              </button>
            </div>

            <Form.Item>
              <Button htmlType="submit" block size="large" loading={loginLoading} className={styles.submitBtn}>
                Sign In
              </Button>
            </Form.Item>
          </Form>
        )}

        {/* ═══ SIGNUP ═══ */}
        {view === "signup" && (
          <Form form={signupForm} name="gate-signup" onFinish={handleSignup} layout="vertical" className={styles.form}>
            <Form.Item name="name" rules={[{ required: true, message: "Please enter your name" }]}>
              <Input placeholder="Full Name" size="large" />
            </Form.Item>

            <Form.Item name="email" rules={[{ required: true, message: "Please enter email" }, { type: "email", message: "Please enter a valid email" }]}>
              <Input placeholder="Email Address" size="large" />
            </Form.Item>

            <Form.Item name="phone" rules={[{ required: true, message: "Please enter phone number" }]}>
              <Input placeholder="Phone Number" size="large" />
            </Form.Item>

            <Form.Item name="company" rules={[{ required: true, message: "Please enter company name" }]}>
              <Input placeholder="Registered Business Name" size="large" />
            </Form.Item>

            <Form.Item name="password" rules={[{ required: true, message: "Please enter password" }, { min: 6, message: "Minimum 6 characters" }]} hasFeedback>
              <Input.Password placeholder="Password" size="large" />
            </Form.Item>

            <Form.Item name="confirmPassword" dependencies={["password"]} hasFeedback rules={[{ required: true, message: "Please confirm password" }, ({ getFieldValue }) => ({ validator(_, value) { if (!value || getFieldValue("password") === value) return Promise.resolve(); return Promise.reject(new Error("Passwords do not match")); } })]}>
              <Input.Password placeholder="Confirm Password" size="large" />
            </Form.Item>

            <Form.Item>
              <Button htmlType="submit" block size="large" loading={signupLoading} className={styles.submitBtn}>
                Create Account
              </Button>
            </Form.Item>
          </Form>
        )}

        {/* ═══ SIGNUP COMPLETE ═══ */}
        {view === "signupComplete" && (
          <div style={{ textAlign: "center", padding: "24px 0" }}>
            <div style={{ fontSize: 40, marginBottom: 12, color: "#C5A028" }}>&#10003;</div>
            <p style={{ fontSize: 18, fontWeight: 700, marginBottom: 10, color: "#111" }}>Application Submitted</p>
            <p style={{ fontSize: 13, color: "#555", lineHeight: 1.7, marginBottom: 8 }}>
              Thank you for registering with The Diamond Counter.
            </p>
            <p style={{ fontSize: 13, color: "#555", lineHeight: 1.7, marginBottom: 20 }}>
              Your account is now <strong>under review</strong>. We typically approve new accounts within <strong>24 hours</strong>. You'll receive an email once approved.
            </p>
            <Button block size="large" className={styles.submitBtn} onClick={goToLogin}>
              Back to Sign In
            </Button>
          </div>
        )}

        {/* ═══ FORGOT PASSWORD ═══ */}
        {view === "forgot" && (
          <Form form={forgotForm} name="gate-forgot" onFinish={handleForgot} layout="vertical" className={styles.form}>
            <p style={{ fontSize: 13, color: "#777", marginBottom: 16, lineHeight: 1.6 }}>
              Enter your email address and we'll send you a link to reset your password.
            </p>

            <Form.Item name="email" rules={[{ required: true, message: "Please enter your email" }, { type: "email", message: "Please enter a valid email" }]}>
              <Input placeholder="Email Address" size="large" />
            </Form.Item>

            <Form.Item>
              <Button htmlType="submit" block size="large" loading={forgotLoading} className={styles.submitBtn}>
                Send Reset Link
              </Button>
            </Form.Item>

            <div style={{ textAlign: "center" }}>
              <button
                type="button"
                onClick={goToLogin}
                style={{ background: "none", border: "none", color: "#C5A028", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
              >
                Back to Sign In
              </button>
            </div>
          </Form>
        )}

        {/* ═══ FORGOT SENT ═══ */}
        {view === "forgotSent" && (
          <div style={{ textAlign: "center", padding: "24px 0" }}>
            <div style={{ fontSize: 40, marginBottom: 12, color: "#C5A028" }}>&#9993;</div>
            <p style={{ fontSize: 13, color: "#555", lineHeight: 1.7, marginBottom: 20 }}>
              If an account exists with that email, we've sent a password reset link. Please check your <strong>inbox</strong> and <strong>spam folder</strong>.
            </p>
            <Button block size="large" className={styles.submitBtn} onClick={goToLogin}>
              Back to Sign In
            </Button>
          </div>
        )}

        {/* ═══ RESET PASSWORD ═══ */}
        {view === "reset" && (
          <Form form={resetForm} name="gate-reset" onFinish={handleReset} layout="vertical" className={styles.form}>
            <p style={{ fontSize: 13, color: "#777", marginBottom: 16, lineHeight: 1.6 }}>
              Enter your new password below.
            </p>

            <Form.Item name="password" rules={[{ required: true, message: "Please enter a password" }, { min: 6, message: "Minimum 6 characters" }]}>
              <Input.Password placeholder="New Password" size="large" />
            </Form.Item>

            <Form.Item name="confirmPassword" rules={[{ required: true, message: "Please confirm your password" }]}>
              <Input.Password placeholder="Confirm Password" size="large" />
            </Form.Item>

            <Form.Item>
              <Button htmlType="submit" block size="large" loading={resetLoading} className={styles.submitBtn}>
                Reset Password
              </Button>
            </Form.Item>

            <div style={{ textAlign: "center" }}>
              <button
                type="button"
                onClick={goToLogin}
                style={{ background: "none", border: "none", color: "#C5A028", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
              >
                Back to Sign In
              </button>
            </div>
          </Form>
        )}

        {/* ═══ RESET SUCCESS ═══ */}
        {view === "resetSuccess" && (
          <div style={{ textAlign: "center", padding: "24px 0" }}>
            <div style={{ fontSize: 40, marginBottom: 12, color: "#C5A028" }}>&#10003;</div>
            <p style={{ fontSize: 18, fontWeight: 700, marginBottom: 10, color: "#111" }}>Password Updated</p>
            <p style={{ fontSize: 13, color: "#555", lineHeight: 1.7, marginBottom: 20 }}>
              Your password has been reset successfully. You can now sign in with your new password.
            </p>
            <Button block size="large" className={styles.submitBtn} onClick={goToLogin}>
              Sign In
            </Button>
          </div>
        )}

        <div className={styles.footer}>
          <span>Luxury Jewellery</span>
          <span className={styles.footerDot}>&bull;</span>
          <span>Certified Diamonds</span>
          <span className={styles.footerDot}>&bull;</span>
          <span>Trusted Craft</span>
        </div>
      </div>
    </div>
  );
}

export default AuthGate;
